import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { api, type CurrentResponse, type HealthResponse, type MatchRow } from "@shared/api";
import { rangeRows } from "@shared/lib/matchAggregate";
import { useAutoRefresh } from "@shared/hooks/useAutoRefresh";
import { applyIncomingMatches } from "@features/dashboard/model/matchUpdates";
import { rangeToFrom, shouldUseDailyBucket } from "@features/dashboard/model/range";
import type { RangeKey, SectionKey } from "@features/dashboard/model/types";

const REFRESH_INTERVAL_MS = 60_000;
const MAX_REFRESH_BACKOFF_MS = 5 * 60_000;
const NEW_MATCH_HIGHLIGHT_MS = 6_500;

interface UseDashboardStateOptions {
  activeSection: SectionKey;
  matchesSectionRef: MutableRefObject<HTMLElement | null>;
}

export function useDashboardState({ activeSection, matchesSectionRef }: UseDashboardStateOptions) {
  const [current, setCurrent] = useState<CurrentResponse | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [allMatches, setAllMatches] = useState<MatchRow[] | null>(null);
  const [range, setRange] = useState<RangeKey>("30d");
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [newMatchIds, setNewMatchIds] = useState<Set<number>>(() => new Set());
  const [newMatchNoticeCount, setNewMatchNoticeCount] = useState(0);
  const knownMatchIdsRef = useRef<Set<number> | null>(null);
  const newMatchTimerRef = useRef<number | undefined>(undefined);

  const from = useMemo(() => rangeToFrom(range), [range]);
  const useDailyBucket = useMemo(() => shouldUseDailyBucket(range), [range]);

  const loadDashboard = useCallback(async () => {
    setError(null);
    const [currentResult, healthResult, matchResult] = await Promise.allSettled([api.current(), api.health(), api.matches(undefined, undefined, 5000)]);
    const failures: string[] = [];

    if (currentResult.status === "fulfilled") setCurrent(currentResult.value);
    else failures.push(resultError("current", currentResult.reason));

    if (healthResult.status === "fulfilled") setHealth(healthResult.value);
    else failures.push(resultError("health", healthResult.reason));

    if (matchResult.status === "fulfilled") {
      applyIncomingMatches(matchResult.value.matches, {
        knownIdsRef: knownMatchIdsRef,
        isMatchLogVisible: activeSection === "matches",
        section: matchesSectionRef.current,
        onNewMatches: (ids, showNotice) => {
          setNewMatchIds(new Set(ids));
          if (newMatchTimerRef.current !== undefined) window.clearTimeout(newMatchTimerRef.current);
          newMatchTimerRef.current = window.setTimeout(() => {
            setNewMatchIds(new Set());
            newMatchTimerRef.current = undefined;
          }, NEW_MATCH_HIGHLIGHT_MS);
          if (showNotice) setNewMatchNoticeCount(ids.length);
          else setNewMatchNoticeCount(0);
        },
      });
      setAllMatches(matchResult.value.matches);
    } else {
      failures.push(resultError("matches", matchResult.reason));
    }

    if (failures.length === 3) throw new Error(failures.join("; "));
    setRefreshKey((key) => key + 1);
    if (failures.length > 0) setError(failures.join("; "));
  }, [activeSection, matchesSectionRef]);

  const {
    isRefreshing,
    lastRefreshAt,
    error: refreshError,
    refreshNow,
  } = useAutoRefresh({
    intervalMs: REFRESH_INTERVAL_MS,
    maxBackoffMs: MAX_REFRESH_BACKOFF_MS,
    refresh: loadDashboard,
  });

  useEffect(() => {
    knownMatchIdsRef.current = null;
    setNewMatchIds(new Set());
    setNewMatchNoticeCount(0);
    setAllMatches(null);
    void refreshNow();
  }, [refreshNow]);

  useEffect(() => {
    return () => {
      if (newMatchTimerRef.current !== undefined) window.clearTimeout(newMatchTimerRef.current);
    };
  }, []);

  const clearNewMatchNotice = useCallback(() => setNewMatchNoticeCount(0), []);
  const matches = useMemo(() => {
    if (allMatches === null) return null;
    return from ? rangeRows(allMatches, from, Number.POSITIVE_INFINITY) : allMatches;
  }, [allMatches, from]);

  return {
    activeRangeLabel: range,
    current,
    displayError: error ?? refreshError,
    error,
    from,
    health,
    isRefreshing,
    lastRefreshAt,
    allMatches,
    matches,
    newMatchIds,
    newMatchNoticeCount,
    range,
    refreshKey,
    refreshNow,
    setRange,
    clearNewMatchNotice,
    useDailyBucket,
  };
}

function resultError(label: string, reason: unknown): string {
  return `${label}: ${reason instanceof Error ? reason.message : String(reason)}`;
}
